/**
 * usePlayerManagement
 *
 * Owns admin player state for the currently selected game: active players,
 * banned players, enable/ban/unban actions, and the ban-confirmation modal
 * state. Wires up all socket listeners and emits required to keep this state
 * in sync with the server.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  GamePlayerInfo,
  GamePlayersResponse,
  TogglePlayerResponse,
  BanPlayerResponse,
  UnbanPlayerResponse,
  BannedPlayerInfo,
  BannedPlayersRequest,
  BannedPlayersResponse,
  GamePlayersRequest,
  type Game,
} from '@swtorbit/shared-socket-events';
import { useGameContext } from '../contexts/GameContext';

type AdminSocket = NonNullable<ReturnType<typeof useSocketContext>['socket']>;

function requestPlayerLists(socket: AdminSocket, gameId: number): void {
  socket.emit(SocketEvent.ADMIN_GET_GAME_PLAYERS, { gameId } satisfies GamePlayersRequest);
  socket.emit(SocketEvent.ADMIN_GET_BANNED_PLAYERS, { gameId } satisfies BannedPlayersRequest);
}

/**
 * Whether a player-list reply belongs to the selected game. Replies to our
 * requests carry `gameId`; live pushes carry only the room code, which an
 * archived game may share with whichever game holds that code now.
 */
function isForSelectedGame(game: Game, data: { roomCode: string; gameId?: number }): boolean {
  if (data.gameId !== undefined) return data.gameId === game.dbId;
  return !game.archived && data.roomCode === game.roomCode;
}

interface BanConfirmState {
  show: boolean;
  playerId: string | null;
}

export interface PlayerManagement {
  players: GamePlayerInfo[];
  bannedPlayers: BannedPlayerInfo[];
  showBanned: boolean;
  setShowBanned: (val: boolean) => void;
  banConfirmState: BanConfirmState;
  refreshPlayers: () => void;
  togglePlayer: (playerId: string, enabled: boolean) => void;
  banPlayer: (playerId: string) => void;
  confirmBan: () => void;
  cancelBan: () => void;
  unbanPlayer: (playerId: string) => void;
}

export function usePlayerManagement(): PlayerManagement {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();

  const [players, setPlayers] = useState<GamePlayerInfo[]>([]);
  const [bannedPlayers, setBannedPlayers] = useState<BannedPlayerInfo[]>([]);
  const [showBanned, setShowBanned] = useState(false);
  const [banConfirmState, setBanConfirmState] = useState<BanConfirmState>({ show: false, playerId: null });

  // Fetch players when selected game changes. Addressed by database id, so
  // this works for archived games too.
  useEffect(() => {
    if (!socket || !selectedGame) {
      setPlayers([]);
      setBannedPlayers([]);
      return;
    }

    requestPlayerLists(socket, selectedGame.dbId);
  }, [socket, selectedGame?.dbId]);

  // Socket listeners for player state changes
  useEffect(() => {
    if (!socket) return;

    const handleGamePlayers = (data: GamePlayersResponse) => {
      if (selectedGame && isForSelectedGame(selectedGame, data)) {
        setPlayers(data.players);
      }
    };

    const handlePlayerToggled = (data: TogglePlayerResponse) => {
      if (data.success && selectedGame && data.roomCode === selectedGame.roomCode) {
        setPlayers((prev) =>
          prev.map((p) =>
            p.playerId === data.playerId ? { ...p, enabled: data.enabled } : p
          )
        );
      }
    };

    const handlePlayerBanned = (data: BanPlayerResponse) => {
      if (data.success && selectedGame && data.roomCode === selectedGame.roomCode) {
        setPlayers((prev) => prev.filter((p) => p.playerId !== data.playerId));
        socket.emit(SocketEvent.ADMIN_GET_BANNED_PLAYERS, { gameId: selectedGame.dbId } satisfies BannedPlayersRequest);
      }
    };

    const handlePlayerUnbanned = (data: UnbanPlayerResponse) => {
      if (data.success && selectedGame && data.roomCode === selectedGame.roomCode) {
        setBannedPlayers((prev) => prev.filter((p) => p.playerId !== data.playerId));
      }
    };

    const handleBannedPlayers = (data: BannedPlayersResponse) => {
      if (selectedGame && isForSelectedGame(selectedGame, data)) {
        setBannedPlayers(data.players);
      }
    };

    const handlePlayerChange = () => {
      // Live room events; an archived game has no players coming and going.
      if (selectedGame && !selectedGame.archived) {
        socket.emit(SocketEvent.ADMIN_GET_GAME_PLAYERS, { gameId: selectedGame.dbId } satisfies GamePlayersRequest);
      }
    };

    socket.on(SocketEvent.ADMIN_GAME_PLAYERS, handleGamePlayers);
    socket.on(SocketEvent.ADMIN_PLAYER_TOGGLED, handlePlayerToggled);
    socket.on(SocketEvent.ADMIN_PLAYER_BANNED, handlePlayerBanned);
    socket.on(SocketEvent.ADMIN_PLAYER_UNBANNED, handlePlayerUnbanned);
    socket.on(SocketEvent.ADMIN_BANNED_PLAYERS, handleBannedPlayers);
    socket.on(SocketEvent.GAME_PLAYER_JOINED, handlePlayerChange);
    socket.on(SocketEvent.GAME_PLAYER_LEFT, handlePlayerChange);
    socket.on(SocketEvent.PLAYER_CHARACTER_NAME_UPDATED, handlePlayerChange);

    return () => {
      socket.off(SocketEvent.ADMIN_GAME_PLAYERS, handleGamePlayers);
      socket.off(SocketEvent.ADMIN_PLAYER_TOGGLED, handlePlayerToggled);
      socket.off(SocketEvent.ADMIN_PLAYER_BANNED, handlePlayerBanned);
      socket.off(SocketEvent.ADMIN_PLAYER_UNBANNED, handlePlayerUnbanned);
      socket.off(SocketEvent.ADMIN_BANNED_PLAYERS, handleBannedPlayers);
      socket.off(SocketEvent.GAME_PLAYER_JOINED, handlePlayerChange);
      socket.off(SocketEvent.GAME_PLAYER_LEFT, handlePlayerChange);
      socket.off(SocketEvent.PLAYER_CHARACTER_NAME_UPDATED, handlePlayerChange);
    };
  }, [socket, selectedGame?.dbId, selectedGame?.roomCode, selectedGame?.archived]);

  const refreshPlayers = useCallback(() => {
    if (!socket || !selectedGame) return;
    requestPlayerLists(socket, selectedGame.dbId);
  }, [socket, selectedGame?.dbId]);

  const togglePlayer = useCallback((playerId: string, enabled: boolean) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.ADMIN_TOGGLE_PLAYER, {
      roomCode: selectedGame.roomCode,
      playerId,
      enabled,
    });
  }, [socket, selectedGame?.roomCode]);

  const banPlayer = useCallback((playerId: string) => {
    if (!socket || !selectedGame) return;
    setBanConfirmState({ show: true, playerId });
  }, [socket, selectedGame?.roomCode]);

  const confirmBan = useCallback(() => {
    if (!socket || !selectedGame || !banConfirmState.playerId) return;
    socket.emit(SocketEvent.ADMIN_BAN_PLAYER, {
      roomCode: selectedGame.roomCode,
      playerId: banConfirmState.playerId,
    });
    setBanConfirmState({ show: false, playerId: null });
  }, [socket, selectedGame?.roomCode, banConfirmState.playerId]);

  const cancelBan = useCallback(() => {
    setBanConfirmState({ show: false, playerId: null });
  }, []);

  const unbanPlayer = useCallback((playerId: string) => {
    if (!socket || !selectedGame) return;
    socket.emit(SocketEvent.ADMIN_UNBAN_PLAYER, {
      roomCode: selectedGame.roomCode,
      playerId,
    });
  }, [socket, selectedGame?.roomCode]);

  return {
    players,
    bannedPlayers,
    showBanned,
    setShowBanned,
    banConfirmState,
    refreshPlayers,
    togglePlayer,
    banPlayer,
    confirmBan,
    cancelBan,
    unbanPlayer,
  };
}
